import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import useRecaptcha from 'hooks/useRecaptcha';
import type { Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import {
  getHotelDestination,
  getHotelInfoRaw,
  hotelsClaude,
  hotelsTripadvisor,
  loadGoogleByProposal,
} from 'services/hotel';
import {
  createDefaultHotelInfoConfig,
  getCityFromHotelSearchGA,
  parseRoomsOccupancy,
  resolveHotelInfo,
  resolveProposalLanguageFromBranch,
} from 'utils/hotel';
import { Hotel as HotelResponse } from 'ts/hotel/interfaces/HotelResponse';
import { Hotel as HotelDetails } from 'ts/hotel/interfaces/HotelDetails';
import { Hotel as BookHotel } from 'ts/hotel/interfaces/BookResponse';
import { flyeastCompanyId } from 'constants/hotel';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

type HotProposal = Proposal['hotels'][number];
type ProposalHotel = HotelResponse | HotelDetails | BookHotel;
type ProposalRoom = ProposalHotel['roomClasses'][number];

const isEnglishCityName = (value: string) => {
  const city = value.trim();
  return /^[\x20-\x7E]+$/.test(city) && /[A-Za-z]/.test(city);
};

const emptyProposal: Proposal = {
  hotels: [],
  flights: [],
  dynamic: [],
  ilPackages: [],
  transfers: [],
};

const getChoosenRoomIds = (item?: HotProposal['item']) => {
  if (!item) return [];

  if (item.choosenRoomIds?.length) {
    return item.choosenRoomIds;
  }

  if (item.choosenRoomId) {
    return [item.choosenRoomId];
  }

  return [];
};

export const useHotelProposal = () => {
  const { hotelIndex } = useContext(HotelConfigContext);
  const { proposal = emptyProposal,
    setProposal,
    user,
    initSiteData } = useContext(MainContext);
  const [urlParams] = useSearchParams();
  const { handleRecapcha } = useRecaptcha();
  const currency = sessionStorage.getItem('displayCurrencyHotels') || 'usd';
  const aiStoreKey = new Date().getTime().toString();
  const defaultLanguage = resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);

  const noop = {
    toggleHotelProposal: async () => {},
    toggleRoomProposal: () => {},
    isHotelInProposal: () => false,
    isRoomInProposal: () => false,
  };

  if (!setProposal) {
    return noop;
  }

  const getPassData = () => {
    const roomsString = urlParams.get('roomsString') || '';
    const lastSearch = sessionStorage.getItem('lasestSearchUrl');

    const occupancy = parseRoomsOccupancy(roomsString);
    const passData: any = {
      checkIn: urlParams.get('checkIn') || '',
      checkOut: urlParams.get('checkOut') || '',
      ...occupancy,
    };

    if (!passData.checkIn && lastSearch) {
      const lastSearchParams = new URLSearchParams(lastSearch?.split('?')[1] || '');
      const lastRoomsString = lastSearchParams.get('roomsString') || '';
      passData.checkIn = lastSearchParams.get('checkIn');
      passData.checkOut = lastSearchParams.get('checkOut');
      Object.assign(passData, parseRoomsOccupancy(lastRoomsString));
    }

    return passData;
  };

  const findProposalHotel = (htuKey: string) =>
    proposal.hotels.find((hotel) => hotel.item.htuKey === htuKey);

  const removeHotelFromProposal = (htuKey: string) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.filter((hotel) => hotel.item.htuKey !== htuKey),
    }));
  };

  const setHotelRoomIds = (htuKey: string, choosenRoomIds: string[]) => {
    if (!choosenRoomIds.length) {
      removeHotelFromProposal(htuKey);
      return;
    }

    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((hotel) =>
        hotel.item.htuKey === htuKey
          ? {
              ...hotel,
              item: {
                ...hotel.item,
                choosenRoomId: choosenRoomIds[0],
                choosenRoomIds,
              },
            }
          : hotel,
      ),
    }));
  };

  const buildNewHotelProposal = async (
    hotel: ProposalHotel,
    choosenRoomIds: string[],
    priceRoom?: ProposalRoom,
  ): Promise<HotProposal> => {
    let result!: HotProposal;

    let city = getCityFromHotelSearchGA(hotelIndex);

    if (!isEnglishCityName(city)) {
      const data = await getHotelDestination(hotel.item.bpid);
      const destCity = (typeof data === 'string' ? data : data?.cityName || data?.city) || '';
      const rawCity =
        destCity ||
        city ||
        urlParams.get('city') ||
        hotel.info.cityName ||
        hotel.addressInfo.city ||
        '';
      city = rawCity;
    }

    const rooms = hotel.roomClasses as ProposalRoom[];
    const priceRoomClass =
      priceRoom || rooms.find((room) => room.rcUniqueKey === choosenRoomIds[0]) || rooms[0];

    result = {
      item: JSON.parse(
        JSON.stringify({
          ...hotel,
          addressInfo: { ...hotel.addressInfo, city },
          info: { ...hotel.info, cityName: city },
          htuIndex: new Date().getTime(),
          choosenRoomId: choosenRoomIds[0],
          choosenRoomIds,
        }),
      ),
      searchToken: sessionStorage.getItem('searchTokenHotels') || '',
      price: (priceRoomClass.price as any)[currency] || priceRoomClass.price.usd,
      currency,
      displayPrice: true,
      ...getPassData(),
    };

    return result;
  };

  const patchProposalHotel = (htuKey: string, patch: Partial<HotProposal>) => {
    setProposal((prev) => ({
      ...prev,
      hotels: prev.hotels.map((hotel) =>
        hotel.item.htuKey === htuKey ? { ...hotel, ...patch } : hotel,
      ),
    }));
  };

  const enrichHotelProposal = (
    newProposal: HotProposal,
    hotel: ProposalHotel,
    roomBToken: string,
  ) => {
    const htuKey = newProposal.item.htuKey;

    (async () => {
      const token = await handleRecapcha();
      const isFlyeast = initSiteData?.companyId === flyeastCompanyId;

      const [googleData, hotelInformation, tripAdvisorData] = await Promise.all([
        token
          ? loadGoogleByProposal(newProposal, defaultLanguage, token, aiStoreKey)
          : Promise.resolve(undefined),
        getHotelInfoRaw({
          hotelUkey: hotel.htuKey,
          searchToken: newProposal.searchToken,
          roomBToken,
          aiStoreKey,
        }),
        hotelsTripadvisor({
          bpid: hotel.item.bpid,
          lang: defaultLanguage,
          aiStoreKey,
        }),
      ]);

      const serpHotel = googleData?.serpResults?.serpHotel;
      const googleImages = serpHotel?.images;
      const hotelInfo = resolveHotelInfo(hotelInformation);
      const hotelInfoImages = hotelInfo?.images || [];

      patchProposalHotel(htuKey, {
        googleData,
        hotelInformation,
        tripAdvisorData,
        googleChecked: {
          reviews: !!serpHotel?.reviews,
          imagesIndexes: (Array.isArray(googleImages) ? googleImages : [])
            .slice(0, isFlyeast ? 0 : 1)
            .map((_, index) => index),
          language: defaultLanguage,
        },
        ...(isFlyeast && hotelInfoImages.length
          ? {
              hotelInfoConfig: {
                ...createDefaultHotelInfoConfig(hotelInfo, defaultLanguage),
                imagesIndexes: hotelInfoImages.slice(0, 1).map((_: any, index: number) => index),
              },
            }
          : {}),
        tripAdvisorChecked: {
          details: false,
          reviews: false,
          amenities: false,
          language: defaultLanguage,
        },
      });

      const claudeData = await hotelsClaude({ aiStoreKey });
      patchProposalHotel(htuKey, { claudeData });
    })();
  };
  const addHotelToProposal = async (
    hotel: ProposalHotel,
    choosenRoomIds: string[],
    priceRoom?: ProposalRoom,
  ) => {
    const newProposal = await buildNewHotelProposal(hotel, choosenRoomIds, priceRoom);
    const roomBToken = priceRoom?.hotelRooms[0].bToken || hotel.roomClasses[0].hotelRooms[0].bToken;

    setProposal({ ...proposal, hotels: [...proposal.hotels, newProposal] });
    enrichHotelProposal(newProposal, hotel, roomBToken);
  };

  const toggleHotelProposal = async (hotel: ProposalHotel) => {
    if (findProposalHotel(hotel.htuKey)) {
      removeHotelFromProposal(hotel.htuKey);
      return;
    }

    const firstRoom = hotel.roomClasses[0] as ProposalRoom;
    await addHotelToProposal(hotel, [firstRoom.rcUniqueKey], firstRoom);
  };

  const isHotelInProposal = (hotel: ProposalHotel) => !!findProposalHotel(hotel.htuKey);

  const isRoomInProposal = (hotel: ProposalHotel, roomClass: ProposalRoom) =>
    getChoosenRoomIds(findProposalHotel(hotel.htuKey)?.item).includes(roomClass.rcUniqueKey);

  const toggleRoomProposal = async (hotel: ProposalHotel, roomClass: ProposalRoom) => {
    const roomKey = roomClass.rcUniqueKey;
    const existing = findProposalHotel(hotel.htuKey);

    if (!existing) {
      await addHotelToProposal(hotel, [roomKey], roomClass);
      return;
    }

    const currentIds = getChoosenRoomIds(existing.item);
    const newIds = currentIds.includes(roomKey)
      ? currentIds.filter((id: any) => id !== roomKey)
      : [...currentIds, roomKey];

    setHotelRoomIds(hotel.htuKey, newIds);
  };

  return {
    toggleHotelProposal,
    toggleRoomProposal,
    isHotelInProposal,
    isRoomInProposal,
  };
};
